import { ReactElement, useMemo } from 'react'

import {
  Stack,
  StackProps,
  Box, 
  Button,
  Table,
  TableProps,
  TableBody,
  TableCell,
  TableCellProps,
  TableRow,
  TableHead,
  Typography,
  Alert
} from '@mui/material'

import {
  OpenDetailIcon,
  DownloadIcon
} from 'shared/icons'

import { SearchWordField } from './searchWordField'
import { ValueSpan } from './valueSpan'

type SearchTableHeaderProps = {
  title?: string
  searchWord: string
  setSearchWord: (word: string) => void
} & StackProps
export const SearchTableHeader = ({
  title,
  searchWord,
  setSearchWord,
  ...others
}: SearchTableHeaderProps) => {
  return (
    <Stack
      position="sticky"
      direction="row"
      justifyContent="space-between"
      spacing={2}
      alignItems="center"
      {...others}
    >
      <Typography flexGrow={1} variant='body1' overflow='hidden' textOverflow='ellipsis' whiteSpace='nowrap'>{title}</Typography>

      <SearchWordField
        id="search-guideline-word-search"
        searchWord={searchWord}
        onChangeSearchWord={setSearchWord}
      />
    </Stack>
  )
}

type SearchTableFooterProps = {
  listLength: number
  listLengthTitle: string
  listLengthUnit: string
  sourceUrl?: string
  onListDownload?: () => void
} & StackProps
export const SearchTableFooter = ({
  listLength,
  listLengthTitle,
  listLengthUnit,
  sourceUrl,
  onListDownload,
  ...others
}: SearchTableFooterProps) => {
  return (
    <Stack position="sticky" direction="row" spacing={2} alignItems='center' {...others}>
      <ValueSpan prefix={listLengthTitle} text={`${listLength}`} suffix={listLengthUnit}/>
      <Box flexGrow={1}/>
      { sourceUrl !== undefined && (
        <Button startIcon={<OpenDetailIcon />} variant="outlined" size='medium' disabled={sourceUrl === ''} href={sourceUrl} target='_blank'>
          参照元を開く
        </Button>
      ) }
      { onListDownload && (
        <Button startIcon={<DownloadIcon />} variant="contained" size='medium' disabled={listLength === 0} onClick={onListDownload}>
          ダウンロード
        </Button>
      ) }
    </Stack>
  )
}

type SearchTableCellProps = {
  value: string | number | null,
  expand?: boolean
} & TableCellProps
const SearchTableCell = ({
  value,
  expand,
  ...others
}: SearchTableCellProps) => {
  const cellStyle = useMemo<TableCellProps["sx"]>(
    () => {
      return value ? (
        expand ? {
          width: '100%',
          wordBreak: 'break-all'
        } : {
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }
      ) : {
        background: 'GhostWhite'
      }
    },
    [value, expand]
  )
  return (
    <TableCell
      {...others}
      sx={cellStyle}
    >
      {value}
    </TableCell>
  )
}

type SearchTableBodyProps<T> = {
  list: T[],
  columnList: {
    label: string
    expand?: boolean
    align?: TableCellProps['align']
    cell?: (record: T, index: number) => ReactElement
    value?: (record: T, index: number) => string | number | null
  }[]
  isDisabled?: (record: T) => boolean
  onClickRow?: (record: T) => void
} & TableProps
export const SearchTableBody = <T,>({
  list,
  columnList,
  isDisabled,
  onClickRow,
  ...others
}: SearchTableBodyProps<T>) => {
  if (list.length === 0) {
    return (
      <Box padding={2}>
        <Alert severity="info" sx={{ wordBreak: 'break-all' }}>
          対象が存在しません。
        </Alert>
      </Box>
    )
  }
  return (
    <Table
      stickyHeader
      size="small"
      sx={{
        '& td, & th': {
          padding: 1
        },
        '& td:first-child, & th:first-child': {
          paddingLeft: 2
        },
        '& td:last-child, & th:first-child': {
          paddingRight: 2
        }
      }}
      {...others}
    >
      <TableHead>
        <TableRow>
          {columnList.map((column, i) =>
            <TableCell
              key={`table-column-${i}`}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {column.label}
            </TableCell>
          )}
        </TableRow>
      </TableHead>
      <TableBody>
        { list.map((record, i) => {
          const disabled = (onClickRow && isDisabled) ? isDisabled(record) : false
          return (
            <TableRow
              key={`table-row-${i}`}
              hover={disabled ? false : true}
              sx={disabled ? {} : { cursor: 'pointer' }}
              onClick={disabled ? undefined : () => {
                if (onClickRow) onClickRow(record)
              }}
            >
              {columnList.map((column, j) =>
                column.cell ? (
                  column.cell(record, i)
                ) : column.value ? (
                  <SearchTableCell
                    key={`table-cell-${i}-${j}`}
                    align={column.align}
                    value={column.value(record, i)}
                    expand={column.expand}
                  />
                ) : (
                  <SearchTableCell key={`table-cell-${i}-${j}`} value=""/>
                )
              )}
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}
